import { playFeedbackSound, playFinalSound, playNextSound } from '@/lib/sound'
// Прохождение урока в предпросмотре. Вся логика перехода и вердикта — здесь,
// компоненты только показывают выданное состояние. Ничего не сохраняется:
// состояние живёт в хуке и умирает вместе с диалогом.
import { useCallback, useEffect, useState } from 'react'
import type { Block } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import { initialAnswer, isAnswered, type AnswerValue } from './answerState'
import { checkAnswer, hasAnswerableInput, type Verdict } from './verdict'

export interface LessonRun {
  index: number
  total: number
  /** `null` — экраны кончились (`isFinished`). */
  block: Block | null
  isFinished: boolean
  answer: AnswerValue
  setAnswer: (next: AnswerValue) => void
  verdict: Verdict | null
  /** Оценивается ли экран вообще (у контентного нечего проверять). */
  isGradable: boolean
  /** Номер ПОСЕЩЕНИЯ экрана — растёт на каждый вход. Своё состояние превью
   *  (отметки сопоставления) принадлежит посещению, а не жизни компонента: два
   *  match-экрана подряд реконсилируются в ОДИН экземпляр, и отметка прошлого
   *  делала бы первый клик на новом обменом карточек. Ключ по блоку разницы не
   *  выражает — «Начать заново» приземляется на тот же блок. */
  visitKey: number
  canCheck: boolean
  check: () => void
  next: () => void
  canGoBack: boolean
  back: () => void
  reset: () => void
}

/**
 * `contentLang` — тот же язык, что рисует превью: индексы вариантов приходят
 * из показанного слота, проверять их надо по нему же.
 *
 * `lessonId` — какой урок проходим. Раньше смену урока выражал React-`key` на
 * всём виде, но он уносил вместе с деревом и шапку, а в ней живёт селектор
 * урока: контрол, которым автор только что выбрал урок, размонтировался под
 * руками, и фокус улетал на контейнер диалога. `null` — прохождение вне
 * диалога (юнит-тесты хука): смену урока тогда объявлять некому.
 */
export const useLessonRun = (
  blocks: Block[],
  contentLang: ContentLang,
  lessonId: number | null = null,
): LessonRun => {
  const [index, setIndex] = useState(0)
  const [answer, setAnswerState] = useState<AnswerValue>(() => initialAnswer(blocks[0] ?? null))
  const [verdict, setVerdict] = useState<Verdict | null>(null)
  const [visitKey, setVisitKey] = useState(0)
  // Блок, под который ответ засеян. Засев на одном монтировании врал на виде,
  // смонтированном раньше блоков (`PreviewLoading`): ответ навсегда оставался
  // `none` — варианты не нажимались, а «Подтвердить» была жива и объявляла
  // неверным вопрос, ответить на который было нельзя.
  const [seededId, setSeededId] = useState<number | null>(() => blocks[0]?.id ?? null)
  const [seededLesson, setSeededLesson] = useState<number | null>(lessonId)
  const [seededLang, setSeededLang] = useState<ContentLang>(contentLang)

  // Смена урока начинает прохождение с первого экрана. Позиция берётся не из
  // `index` напрямую, а через `shownIndex`: состояние правится в этом же рендере,
  // и до его применения `index` ещё указывает в ЧУЖОЙ (прошлый) урок.
  const lessonChanged = lessonId !== seededLesson
  const shownIndex = lessonChanged ? 0 : index
  const block = blocks[shownIndex] ?? null
  const seed = useCallback((next: Block | null) => {
    setSeededId(next?.id ?? null)
    setAnswerState(initialAnswer(next))
    setVerdict(null)
    setVisitKey((v) => v + 1)
  }, [])

  // Правка состояния в рендере (приём React): приехавший позже экран обязан
  // получить засев в том же кадре, иначе автор нажимает по чужому состоянию.
  if (lessonChanged) {
    setSeededLesson(lessonId)
    setIndex(0)
  }
  if (contentLang !== seededLang) {
    // Смена языка — тот же экран, показанный ВТОРЫМ слотом: `index` не трогаем,
    // а ответ и вердикт обнуляем. `AnswerValue` хранит ИНДЕКСЫ вариантов
    // показанного слота, а у второго языка их число и порядок свои — вердикт
    // «Правильно» встал бы над вариантом, которого автор не выбирал.
    setSeededLang(contentLang)
    seed(block)
  } else if ((block?.id ?? null) !== seededId) {
    seed(block)
  }
  // Оценивается не «тип блока», а экран, на котором есть чем ответить: квиз
  // без вариантов иначе запирал бы репетицию вечно погашенной кнопкой.
  const isGradable = block !== null && hasAnswerableInput(block, contentLang)

  // Засев зовётся ЯВНО, а не выводится из смены блока: «Начать заново»
  // приземляется на тот же блок, и ответ там всё равно обязан обнулиться.
  const goTo = useCallback(
    (next: number) => {
      setIndex(next)
      seed(blocks[next] ?? null)
    },
    [blocks, seed],
  )

  const setAnswer = useCallback(
    (next: AnswerValue) => {
      // Проверенный ответ заморожен: иначе автор переспрашивал бы экран
      // поверх уже показанного вердикта и видел бы чужую подсветку.
      if (verdict !== null || !block) return
      setAnswerState(next)
      // Да/нет отвечает сразу по нажатию — в плеере у него нет кнопки
      // проверки вовсе (`YesNoBlock` не передаёт `onSubmit` в футер).
      if (next.kind === 'yesNo' && next.value !== null)
        setVerdict(checkAnswer(block, next, contentLang))
    },
    [block, verdict, contentLang],
  )

  // Звук вердикта, как в плеере: «принято» (опрос) звука не даёт.
  useEffect(() => {
    if (verdict === 'correct' || verdict === 'incorrect') playFeedbackSound(verdict === 'correct')
  }, [verdict])

  const canCheck = isGradable && verdict === null && isAnswered(answer)

  const check = useCallback(() => {
    if (!canCheck || !block) return
    setVerdict(checkAnswer(block, answer, contentLang))
  }, [canCheck, block, answer, contentLang])

  const next = useCallback(() => {
    if (shownIndex >= blocks.length) return
    // Как в плеере: «Далее» звучит на переходе, а последний экран — звуком конца урока.
    if (shownIndex + 1 >= blocks.length) playFinalSound()
    else playNextSound()
    goTo(shownIndex + 1)
  }, [shownIndex, blocks.length, goTo])

  const back = useCallback(() => {
    if (shownIndex > 0) goTo(shownIndex - 1)
  }, [shownIndex, goTo])

  const reset = useCallback(() => goTo(0), [goTo])

  return {
    index: shownIndex,
    total: blocks.length,
    block,
    isFinished: blocks.length > 0 && shownIndex >= blocks.length,
    answer,
    setAnswer,
    verdict,
    isGradable,
    visitKey,
    canCheck,
    check,
    next,
    canGoBack: shownIndex > 0,
    back,
    reset,
  }
}
